import {
  DEFAULT_MODELING_FORMAT,
  MODEL_GRID_SIZE,
  StickyNodeSizeByModelingFormat,
  VIEW_GRID_SIZE,
  VIEW_MODEL_CANVAS_BOUNDS,
  type AIViewModelContextLinkBend,
  type AIViewModelContextView,
  type AIViewModelPatchView,
} from 'balus-common';
import Victor from 'victor';
import { ConnectionPointMargin } from '@model-framework/link/constants';
import { RectShape } from '@model-framework/shape/RectShape';
import { Rect as BasicRect } from '@view-model/models/common/basic/Rect';
import { Point } from '@view-model/models/common/basic/Point';
import { Size } from '@view-model/models/common/basic/Size';
import { ElementDescriptionLayout } from '@view-model/models/sticky/ElementDescription/components/ElementDescriptionLayout';
import { StickyZoneSize } from '@view-model/models/sticky/StickyZoneView/domain/StickyZoneSize';
import type { Position } from '@view-model/models/common/types/ui';
import { buildDisplayOrderTreeByContainment } from '@view-model/models/framework/display-order/domain/DisplayOrderTree/buildDisplayOrderTreeByContainment';
import { buildLinkBezierLine } from '@view-model/models/sticky/StickyLink/domain/linkBezierLine';
import { linkStartEndPoints } from '@view-model/models/sticky/StickyLink/domain/linkStartEndPoints';
import { LinkBend } from '@view-model/models/sticky/StickyLink/domain/vo/LinkBend';

/**
 * ビューの品質チェック（レイアウト・スキーマ整合）。
 *
 * クライアントで実行するのは、ビュー全体の状態を持つため「新規・移動した要素が既存要素と
 * 重なる」といった状態依存の検査ができるから。
 */

/**
 * 警告の種類。適用ゲートで「適用を止めるか（blocking）／適用して注意を返すか（advisory）」を
 * 判定するために使う。
 */
type QualityWarningKind =
  // ID重複。同じIDの要素が複数あり、そのまま適用するとデータが壊れる
  | 'duplicateId'
  // 存在しない要素を指すリンク。そのまま適用するとデータが壊れる
  | 'linkReference'
  // 重なり・マージン・サイズ・グリッド・ビュー範囲など、見た目の粗さ
  | 'layout';

interface QualityWarning {
  path: string;
  message: string;
  kind: QualityWarningKind;
}

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function rectOverlapsEllipse(rect: Rect, bounds: Rect): boolean {
  const radiusX = bounds.width / 2;
  const radiusY = bounds.height / 2;
  const centerX = bounds.x + radiusX;
  const centerY = bounds.y + radiusY;
  const nearestX = Math.min(Math.max(centerX, rect.x), rect.x + rect.width);
  const nearestY = Math.min(Math.max(centerY, rect.y), rect.y + rect.height);
  return ((nearestX - centerX) / radiusX) ** 2 + ((nearestY - centerY) / radiusY) ** 2 < 1;
}

function rectsWithinMargin(a: Rect, b: Rect, margin: number): boolean {
  const expandedA: Rect = {
    x: a.x - margin,
    y: a.y - margin,
    width: a.width + margin * 2,
    height: a.height + margin * 2,
  };
  return rectsOverlap(expandedA, b);
}

function isInsideWithMargin(inner: Rect, viewSize: { width: number; height: number }, margin: number): boolean {
  return (
    inner.x >= margin &&
    inner.y >= margin &&
    inner.x + inner.width <= viewSize.width - margin &&
    inner.y + inner.height <= viewSize.height - margin
  );
}

function exceedsRightOrBottomEdge(inner: Rect, viewSize: { width: number; height: number }, margin: number): boolean {
  return inner.x + inner.width > viewSize.width - margin || inner.y + inner.height > viewSize.height - margin;
}

type QualityCheckOptions = {
  // 付箋の期待サイズ。ModelingFormat (CJK/LATIN) によって異なる
  nodeSize?: { width: number; height: number };
};

function resolveNodeSize(options?: QualityCheckOptions): { width: number; height: number } {
  return options?.nodeSize ?? StickyNodeSizeByModelingFormat[DEFAULT_MODELING_FORMAT];
}

// 位置がモデルグリッドに乗っているかチェックする共通処理
function checkPositionOnGrid(
  warnings: QualityWarning[],
  path: string,
  label: string,
  position: { x: number; y: number }
): void {
  if (position.x % MODEL_GRID_SIZE !== 0 || position.y % MODEL_GRID_SIZE !== 0) {
    warnings.push({
      path,
      message: `${label}の位置が${MODEL_GRID_SIZE}の倍数ではありません: (${position.x}, ${position.y})`,
      kind: 'layout',
    });
  }
}

// キャンバス上でビュー同士に確保するマージン(px)
const VIEW_MIN_MARGIN = VIEW_GRID_SIZE;

// ビューの端から内部要素までに確保するマージン(px)
const VIEW_EDGE_MARGIN = VIEW_GRID_SIZE;

// ゾーンの上端から内部要素までに確保するマージン(px)。ゾーン名が内部要素と重なる形で
// 上部に表示されるため、上端だけ広く取る。
// modeling Skill (backend/resources/skills/modeling.md) のレイアウトルールと一致させること。
const ZONE_TOP_MARGIN = MODEL_GRID_SIZE * 3;

// ゾーンの左右・下端から内部要素までに確保するマージン(px)
const ZONE_INNER_MARGIN = MODEL_GRID_SIZE;

// 内部要素を収めるのに必要な最小サイズから、この値までの超過は意図的な余白として許容する(px)。
// これを超えて大きいゾーンは「必要以上に大きい」とみなす。
const ZONE_SIZE_ALLOWANCE = MODEL_GRID_SIZE * 4;

// リンクでつなぐ要素の枠同士に確保する間隔(px)。リンクの端点は要素の枠から24px外側に置かれ、
// 矢印マークだけで32px使うため、これを下回ると線がマークに埋もれて関係の向きが読み取れなくなる。
const LINK_MIN_ENDPOINT_GAP = MODEL_GRID_SIZE * 2;

// リンクのラベルの描画に関する定数。webapp の LinkTextView と一致させること。
// ラベルは36pxの太字で描画される。自動折り返しはされず、改行を入れない限り1行になる。
const LINK_LABEL_FONT_SIZE = 36;
// 全角以外の文字の概算幅(px)。太字の欧文はおよそフォントサイズの0.55倍の幅になる
const LINK_LABEL_HALF_WIDTH = 20;
// ラベルの左右パディング(px-[18px])の合計
const LINK_LABEL_PADDING_X = 36;
// ラベルの最小幅(px)。文字が短くてもこの幅の背景が描画される
const LINK_LABEL_MIN_WIDTH = 180;
// ラベル1行分の高さ(px)。36pxの文字が line-height: normal で描画された場合の概算
const LINK_LABEL_LINE_HEIGHT = 44;

// 補足説明（吹き出し）の本文の描画に関する定数。webapp の CalloutText と一致させること。
// 本文は text-3xl(32px) で描画され、行の高さは line-height 1.5 を引き継ぐ
const ELEMENT_DESCRIPTION_FONT_SIZE = 32;
const ELEMENT_DESCRIPTION_HALF_WIDTH = 18;
const ELEMENT_DESCRIPTION_LINE_HEIGHT = 48;
// 本文の周囲のパディング（外側 p-5 と内側 p-2）の片側分
const ELEMENT_DESCRIPTION_PADDING = 28;

type PlacedView = {
  id: string;
  name: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
};

function toRect(element: { position: { x: number; y: number }; size: { width: number; height: number } }): Rect {
  return {
    x: element.position.x,
    y: element.position.y,
    width: element.size.width,
    height: element.size.height,
  };
}

function rectContains(outer: Rect, inner: Rect): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

type ZoneSizeCheckTarget = {
  id: string;
  name: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
};

type ZoneContentCandidate = {
  id: string;
  label: string;
  // ゾーン同士の重なりは専用の検査で報告するため、ゾーンかどうかを区別する
  isZone: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
};

/** ゾーンの内包判定・内側マージン検査の対象になる要素を、ビューの要素から組み立てる。 */
function toZoneContentCandidates(elements: {
  nodes: { id: string; content: string; position: { x: number; y: number }; size: { width: number; height: number } }[];
  zones: ZoneSizeCheckTarget[];
  descriptionPanels: { id: string; position: { x: number; y: number }; size: { width: number; height: number } }[];
}): ZoneContentCandidate[] {
  return [
    ...elements.nodes.map((node) => ({
      id: node.id,
      label: `付箋"${node.content}"`,
      isZone: false,
      position: node.position,
      size: node.size,
    })),
    ...elements.zones.map((zone) => ({
      id: zone.id,
      label: `ゾーン"${zone.name}"`,
      isZone: true,
      position: zone.position,
      size: zone.size,
    })),
    ...elements.descriptionPanels.map((panel) => ({
      id: panel.id,
      label: '説明パネル',
      isZone: false,
      position: panel.position,
      size: panel.size,
    })),
  ];
}

/**
 * ゾーンが内部要素に対して必要以上に大きくないかを検査する。
 *
 * ゾーンのサイズはAIが決めた値がそのまま使われる。他のレイアウトルールは「内部要素が収まる」
 * という下限しか課さないため、上限側を押さえないと中身に対して極端に大きいゾーンができる。
 *
 * 内包判定はゾーンの矩形に完全に含まれるかどうかで行う。ネストしたゾーンの場合、外側のゾーンは
 * 内側のゾーンごと内包要素として数えるため、必要サイズは正しく求まる。内包する要素が無いゾーンは
 * 必要サイズを決められないので対象外とする。
 */
function checkZoneSizeAgainstContents(
  zones: ZoneSizeCheckTarget[],
  candidates: ZoneContentCandidate[],
  viewPath: string
): QualityWarning[] {
  const warnings: QualityWarning[] = [];

  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];
    const zoneRect = toRect(zone);
    const contents = candidates.filter(
      (candidate) => candidate.id !== zone.id && rectContains(zoneRect, toRect(candidate))
    );
    if (contents.length === 0) continue;

    const left = Math.min(...contents.map((content) => content.position.x));
    const top = Math.min(...contents.map((content) => content.position.y));
    const right = Math.max(...contents.map((content) => content.position.x + content.size.width));
    const bottom = Math.max(...contents.map((content) => content.position.y + content.size.height));
    const requiredWidth = right - left + ZONE_INNER_MARGIN * 2;
    const requiredHeight = bottom - top + ZONE_TOP_MARGIN + ZONE_INNER_MARGIN;

    if (zone.size.width > requiredWidth + ZONE_SIZE_ALLOWANCE) {
      warnings.push({
        path: `${viewPath}.zones[${i}].size.width`,
        message:
          `ゾーン"${zone.name}"の幅が内部要素に対して大きすぎます: 内部要素を収めるのに必要な幅は${requiredWidth}pxですが${zone.size.width}pxあります。` +
          `左右のマージン${ZONE_INNER_MARGIN}pxを保ったまま、幅を${requiredWidth}px程度まで縮めてください`,
        kind: 'layout',
      });
    }
    if (zone.size.height > requiredHeight + ZONE_SIZE_ALLOWANCE) {
      warnings.push({
        path: `${viewPath}.zones[${i}].size.height`,
        message:
          `ゾーン"${zone.name}"の高さが内部要素に対して大きすぎます: 内部要素を収めるのに必要な高さは${requiredHeight}pxですが${zone.size.height}pxあります。` +
          `上端${ZONE_TOP_MARGIN}px・下端${ZONE_INNER_MARGIN}pxのマージンを保ったまま、高さを${requiredHeight}px程度まで縮めてください`,
        kind: 'layout',
      });
    }
  }

  return warnings;
}

/**
 * ゾーンの枠と内部要素の間隔（内側マージン）を検査する。
 *
 * ゾーンの枠に張り付いた付箋はグループの境界が読み取りづらく、ゾーン上部はゾーン名と重なって
 * 文字が読めなくなる。ゾーンからはみ出している要素（枠をまたぐ要素）も、どのゾーンに属するのかが
 * 曖昧になるため同時に検出する。
 *
 * ネストしたゾーンの場合、内部要素は最も内側のゾーンとの間隔だけを見る。外側のゾーンとの間隔は、
 * 内側のゾーン自身がマージンを確保していれば自然に満たされるため。
 *
 * targetIds を渡すと、その要素が関係する組み合わせ（ゾーンか内部要素のどちらかが対象）だけを
 * 報告する。パッチの適用ゲートで、触っていない既存要素の指摘を返さないために使う。
 */
function checkZoneInnerMargins(
  zones: ZoneSizeCheckTarget[],
  candidates: ZoneContentCandidate[],
  viewPath: string,
  targetIds?: Set<string>
): QualityWarning[] {
  const warnings: QualityWarning[] = [];

  for (const content of candidates) {
    const contentRect = toRect(content);
    let container: ZoneSizeCheckTarget | undefined;

    for (const zone of zones) {
      if (zone.id === content.id) continue;
      if (targetIds && !targetIds.has(zone.id) && !targetIds.has(content.id)) continue;

      const zoneRect = toRect(zone);
      if (rectContains(zoneRect, contentRect)) {
        // 最も内側のゾーン（面積が最小のもの）を内部要素の所属先とみなす
        if (!container || zone.size.width * zone.size.height < container.size.width * container.size.height) {
          container = zone;
        }
        continue;
      }
      // ゾーン同士の重なりは専用の検査が報告するので、ここでは重複して報告しない
      if (!content.isZone && rectsOverlap(zoneRect, contentRect)) {
        warnings.push({
          path: `${viewPath}.zones`,
          message:
            `${content.label}がゾーン"${zone.name}"の枠をまたいではみ出しています。` +
            `ゾーンの内側に収める場合は上端${ZONE_TOP_MARGIN}px・左右下端${ZONE_INNER_MARGIN}px以上のマージンを確保し、` +
            `ゾーンの外に置く場合はゾーンと重ならない位置へ移動してください`,
          kind: 'layout',
        });
      }
    }

    if (!container) continue;

    const containerRect = toRect(container);
    const gaps = [
      { label: '上端', gap: contentRect.y - containerRect.y, required: ZONE_TOP_MARGIN },
      { label: '左端', gap: contentRect.x - containerRect.x, required: ZONE_INNER_MARGIN },
      {
        label: '右端',
        gap: containerRect.x + containerRect.width - (contentRect.x + contentRect.width),
        required: ZONE_INNER_MARGIN,
      },
      {
        label: '下端',
        gap: containerRect.y + containerRect.height - (contentRect.y + contentRect.height),
        required: ZONE_INNER_MARGIN,
      },
    ];
    const violations = gaps.filter(({ gap, required }) => gap < required);
    if (violations.length === 0) continue;

    warnings.push({
      path: `${viewPath}.zones`,
      message:
        `${content.label}がゾーン"${container.name}"の枠に近すぎます: ` +
        violations.map(({ label, gap, required }) => `${label}から${gap}px（${required}px以上必要）`).join('、') +
        `。ゾーンを広げるか内部要素を動かしてマージンを確保してください` +
        (violations.some(({ label }) => label === '上端')
          ? `（ゾーン上部はゾーン名が重なって表示されるため${ZONE_TOP_MARGIN}px以上必要です）`
          : ''),
      kind: 'layout',
    });
  }

  return warnings;
}

type LinkEndpointElement = {
  // 警告メッセージで要素を指すためのラベル（例: 付箋"A"）
  label: string;
  // ラベルの重なりを報告するのは付箋との間だけにするための区別。ゾーンは背景として描かれ、
  // ラベルは不透明な背景を持つため、ゾーンの上に載っても文字は読める
  isNode: boolean;
  rect: Rect;
};

/** リンクの端点になりうる要素（付箋・ゾーン）をIDで引けるようにする。 */
function toLinkEndpointMap(elements: {
  nodes: { id: string; content: string; position: { x: number; y: number }; size: { width: number; height: number } }[];
  zones: { id: string; name: string; position: { x: number; y: number }; size: { width: number; height: number } }[];
}): Map<string, LinkEndpointElement> {
  const endpoints = new Map<string, LinkEndpointElement>();
  for (const node of elements.nodes) {
    endpoints.set(node.id, { label: `付箋"${node.content}"`, isNode: true, rect: toRect(node) });
  }
  for (const zone of elements.zones) {
    endpoints.set(zone.id, { label: `ゾーン"${zone.name}"`, isNode: false, rect: toRect(zone) });
  }
  return endpoints;
}

function rectCenter(rect: Rect): { x: number; y: number } {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

/** 2つの矩形の軸ごとの隙間。その軸で重なっている場合は負の値になる。 */
function axisGaps(a: Rect, b: Rect): { x: number; y: number } {
  return {
    x: Math.max(b.x - (a.x + a.width), a.x - (b.x + b.width)),
    y: Math.max(b.y - (a.y + a.height), a.y - (b.y + b.height)),
  };
}

/** rect の中心から target へ向かう直線が rect の枠と交わる点を返す。リンクの端点はこの位置に置かれる。 */
function borderPointToward(rect: Rect, target: { x: number; y: number }): { x: number; y: number } {
  const center = rectCenter(rect);
  const dx = target.x - center.x;
  const dy = target.y - center.y;
  if (dx === 0 && dy === 0) return center;

  const scaleX = dx === 0 ? Infinity : rect.width / 2 / Math.abs(dx);
  const scaleY = dy === 0 ? Infinity : rect.height / 2 / Math.abs(dy);
  const scale = Math.min(scaleX, scaleY);
  return { x: center.x + dx * scale, y: center.y + dy * scale };
}

// 全角として描画される文字かどうかの概算判定。ASCII と Latin-1 の範囲だけを半角として扱う
function isFullWidthChar(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code > 0x00ff;
}

/**
 * リンクのラベルの表示サイズを概算する。
 *
 * ラベルは自動折り返しされず、改行だけが改行として描画されるため、幅は最も長い行の文字数にそのまま
 * 比例する。全角文字はフォントサイズと同じ幅、それ以外はおよそ半分の幅として見積もる。
 */
function estimateLinkLabelSize(label: string): { width: number; height: number } {
  const lines = label.split('\n');
  const textWidth = Math.max(
    ...lines.map((line) =>
      [...line].reduce(
        (width, char) => width + (isFullWidthChar(char) ? LINK_LABEL_FONT_SIZE : LINK_LABEL_HALF_WIDTH),
        0
      )
    )
  );
  return {
    width: Math.max(LINK_LABEL_MIN_WIDTH, textWidth + LINK_LABEL_PADDING_X),
    height: lines.length * LINK_LABEL_LINE_HEIGHT,
  };
}

type LinkForSpacing = {
  id: string;
  from: string;
  to: string;
  label?: string;
  bend?: AIViewModelContextLinkBend;
};

type LinkLabelPlacement = {
  linkName: string;
  rect: Rect;
  isTargeted: boolean;
};

type PlacedLinkLabel = {
  linkName: string;
  rect: Rect;
  relatedIds: string[];
};

function linkDisplayName(label: string | undefined, from: LinkEndpointElement, to: LinkEndpointElement): string {
  return label ? `リンク"${label}"` : `${from.label}と${to.label}をつなぐリンク`;
}

function placeLinkLabels(links: LinkForSpacing[], endpoints: Map<string, LinkEndpointElement>): PlacedLinkLabel[] {
  return links.flatMap((link) => {
    const label = link.label?.trim();
    const from = endpoints.get(link.from);
    const to = endpoints.get(link.to);
    // 端点間隔の検査と違い、端点同士が重なっていてもラベルは描画されるため除外しない
    if (!label || link.from === link.to || !from || !to) return [];

    return [
      {
        linkName: linkDisplayName(label, from, to),
        rect: toLabelRect(linkBendingPoint(from.rect, to.rect, link.bend), estimateLinkLabelSize(label)),
        relatedIds: [link.id, link.from, link.to],
      },
    ];
  });
}

/**
 * ベンディングコントロールの位置を返す。曲線はこの点を通り、ラベルもこの点に重ねて描画される。
 *
 * リンクは bend に応じて曲がるため、両端を結ぶ直線の中点で判定すると実際の表示位置とずれる。
 *
 * 端点の算出に LinkPlacement ではなく linkStartEndPoints を使うのは、LinkPlacement が
 * BezierQuadraticFactory 経由で StickyNode（jotai ストア）に依存しており、描画を伴わない
 * この検査から画面の初期化まで引き込まれるため。
 */
function linkBendingPoint(fromRect: Rect, toRect: Rect, bend: AIViewModelContextLinkBend | undefined): Victor {
  const [startingPoint, endingPoint] = linkStartEndPoints(
    Victor.fromObject(rectCenter(fromRect)),
    Victor.fromObject(rectCenter(toRect)),
    new RectShape({ width: fromRect.width, height: fromRect.height }),
    new RectShape({ width: toRect.width, height: toRect.height }),
    ConnectionPointMargin
  );

  return (bend ? new LinkBend(bend) : LinkBend.Straight()).bendingPoint(startingPoint, endingPoint);
}

function isStraightBend(bend: AIViewModelContextLinkBend | undefined): boolean {
  return !bend || (bend.r === 0.5 && bend.d === 0);
}

function toLabelRect(center: { x: number; y: number }, labelSize: { width: number; height: number }): Rect {
  return {
    x: center.x - labelSize.width / 2,
    y: center.y - labelSize.height / 2,
    width: labelSize.width,
    height: labelSize.height,
  };
}

/**
 * リンクが読み取れる長さを持っているか、ラベルが他の要素と重ならずに収まるかを検査する。
 *
 * 端点の要素が近すぎるリンクは、線のほとんどが矢印マークで埋まって向きや線種が読み取れない。
 * さらにラベルは不透明な矩形として重ねて描画されるため、重なった相手の文字ごと読めなくなる。
 * ラベルは自動折り返しされないので、必要な間隔はラベルの文字数に応じて変わる。
 *
 * targetIds を渡すと、そのリンクか端点が対象に含まれるものだけを報告する。パッチの適用ゲートで、
 * 触っていない既存要素の指摘を返さないために使う。ラベルが端点以外の付箋と重なる指摘だけは、
 * その付箋を動かしたときにも報告する必要があるため、重なった付箋も対象として扱う。
 */
function checkLinkSpacing(
  links: LinkForSpacing[],
  endpoints: Map<string, LinkEndpointElement>,
  viewPath: string,
  targetIds?: Set<string>
): QualityWarning[] {
  const warnings: QualityWarning[] = [];
  const labelPlacements: LinkLabelPlacement[] = [];

  for (const link of links) {
    if (link.from === link.to) continue;

    const from = endpoints.get(link.from);
    const to = endpoints.get(link.to);
    // 端点が見つからないリンクは参照整合性の検査が報告する
    if (!from || !to) continue;
    // 端点同士が重なっている場合は重なりの検査が報告するので、ここでは扱わない
    if (rectsOverlap(from.rect, to.rect)) continue;

    const isTargeted = !targetIds || targetIds.has(link.id) || targetIds.has(link.from) || targetIds.has(link.to);
    const label = link.label?.trim();
    const linkName = linkDisplayName(label, from, to);
    const fromPoint = borderPointToward(from.rect, rectCenter(to.rect));
    const toPoint = borderPointToward(to.rect, rectCenter(from.rect));
    // 枠同士の間隔（リンクの線が引かれる区間の長さ）
    const gap = Math.round(Math.hypot(toPoint.x - fromPoint.x, toPoint.y - fromPoint.y));
    const isHorizontal = Math.abs(toPoint.x - fromPoint.x) >= Math.abs(toPoint.y - fromPoint.y);
    const labelSize = label ? estimateLinkLabelSize(label) : undefined;
    // ラベルを残す場合に必要な間隔。並べる向きによって効いてくる辺が変わる
    const requiredForLabel = labelSize && (isHorizontal ? labelSize.width : labelSize.height);

    if (gap < LINK_MIN_ENDPOINT_GAP) {
      if (isTargeted) {
        warnings.push({
          path: `${viewPath}.links`,
          message:
            `${linkName}が短すぎて線と矢印が読み取れません: ${from.label}と${to.label}の枠の間隔が${gap}pxしかありません（${LINK_MIN_ENDPOINT_GAP}px以上必要）。` +
            `どちらかの要素を動かして間隔を空けてください` +
            (requiredForLabel !== undefined
              ? `（ラベルを残す場合は、ラベルの表示に必要な${isHorizontal ? '横' : '縦'}方向約${requiredForLabel}px以上の間隔が必要です）`
              : ''),
          kind: 'layout',
        });
      }
      continue;
    }

    if (!labelSize) continue;

    const labelRect = toLabelRect(linkBendingPoint(from.rect, to.rect, link.bend), labelSize);
    labelPlacements.push({ linkName, rect: labelRect, isTargeted });

    const collided = [from, to].filter((endpoint) => endpoint.isNode && rectsOverlap(labelRect, endpoint.rect));
    if (isTargeted && collided.length > 0) {
      const collidedLabels = collided.map((endpoint) => endpoint.label).join('と');
      // 必要な間隔（ラベルの幅・高さ）と揃うように、比較対象も同じ向きの隙間で示す
      const axisLabel = isHorizontal ? '横' : '縦';
      const gaps = axisGaps(from.rect, to.rect);
      const axisGap = Math.max(0, Math.round(isHorizontal ? gaps.x : gaps.y));
      // 曲げているリンクは間隔が足りていてもラベルが端点側へ寄って重なるため、軸方向の間隔で
      // 説明すると「必要な間隔より広いのに重なっている」という矛盾した文になる
      const cause = isStraightBend(link.bend)
        ? `このラベルの表示には${axisLabel}方向に約${requiredForLabel}pxの間隔が必要ですが、` +
          `${from.label}と${to.label}の${axisLabel}方向の間隔は${axisGap}pxです。`
        : '曲げ(bend)で決まるラベルの位置が端点に寄っています。';

      warnings.push({
        path: `${viewPath}.links`,
        message:
          `${linkName}のラベルが${collidedLabels}に重なって読めません: ` +
          cause +
          `リンクの曲げ(bend)を調整してラベルの位置をずらすか、両端の要素をもっと離すか、` +
          `ラベルを短くするか、ラベルを外してください`,
        kind: 'layout',
      });
    }

    for (const [elementId, element] of endpoints) {
      if (!element.isNode || elementId === link.from || elementId === link.to) continue;
      if (!isTargeted && !targetIds?.has(elementId)) continue;
      if (!rectsOverlap(labelRect, element.rect)) continue;

      warnings.push({
        path: `${viewPath}.links`,
        message:
          `${linkName}のラベルが、端点ではない${element.label}に重なって読めません: ` +
          `その付箋か両端の要素を動かすか、リンクを曲げてラベルの位置をずらしてください`,
        kind: 'layout',
      });
    }
  }

  for (let index = 0; index < labelPlacements.length; index++) {
    const first = labelPlacements[index];
    for (let otherIndex = index + 1; otherIndex < labelPlacements.length; otherIndex++) {
      const second = labelPlacements[otherIndex];
      if (!first.isTargeted && !second.isTargeted) continue;
      if (!rectsOverlap(first.rect, second.rect)) continue;

      warnings.push({
        path: `${viewPath}.links`,
        message:
          `${first.linkName}と${second.linkName}のラベル同士が重なって読めません: ` +
          `どちらかのリンクを曲げるか、両端の要素を動かしてラベルを離してください`,
        kind: 'layout',
      });
    }
  }

  return warnings;
}

function toBasicRect(rect: Rect): BasicRect {
  return BasicRect.fromXYWH(rect.x, rect.y, rect.width, rect.height);
}

function isFiniteRect(rect: Rect): boolean {
  return [rect.x, rect.y, rect.width, rect.height].every(Number.isFinite);
}

// リンクの曲線を折れ線に近似するときの分割数。BezierLine の交差判定は当たるまで曲線を1px単位へ
// 再帰分割するため、スキーマ上有効な範囲の大きな bend や座標を渡すと計算量とメモリが青天井になる。
const CROSSING_CURVE_SEGMENTS = 64;

/**
 * 線分と矩形が交差するかを Liang-Barsky で判定する。線分が矩形へ完全に含まれる場合も真を返す。
 */
function segmentIntersectsRect(start: Position, end: Position, rect: Rect): boolean {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  let entering = 0;
  let leaving = 1;

  const clip = (direction: number, distance: number): boolean => {
    if (direction === 0) return distance >= 0;

    const ratio = distance / direction;
    if (direction < 0) {
      if (ratio > leaving) return false;
      if (ratio > entering) entering = ratio;
    } else {
      if (ratio < entering) return false;
      if (ratio < leaving) leaving = ratio;
    }
    return true;
  };

  return (
    clip(-dx, start.x - rect.x) &&
    clip(dx, rect.x + rect.width - start.x) &&
    clip(-dy, start.y - rect.y) &&
    clip(dy, rect.y + rect.height - start.y)
  );
}

/**
 * 描画と同じベジエ曲線を等間隔に分割した折れ線を返す。有限な曲線を組めない場合は undefined を返す。
 */
function sampleLinkCurve(
  fromRect: Rect,
  toRect: Rect,
  bend: AIViewModelContextLinkBend | undefined,
  nodeSize: { width: number; height: number }
): Position[] | undefined {
  const bezierLine = buildLinkBezierLine(
    toBasicRect(fromRect),
    toBasicRect(toRect),
    linkBendingPoint(fromRect, toRect, bend),
    ConnectionPointMargin,
    nodeSize
  );
  const points = Array.from({ length: CROSSING_CURVE_SEGMENTS + 1 }, (_, index) => {
    const { x, y } = bezierLine.getPoint(index / CROSSING_CURVE_SEGMENTS);
    return { x, y };
  });

  // 極端な bend や座標では制御点が表現できる範囲を超え、折れ線が座標を失う
  return points.every(({ x, y }) => Number.isFinite(x) && Number.isFinite(y)) ? points : undefined;
}

function curveBoundingRect(curve: Position[]): Rect {
  const left = curve.reduce((value, { x }) => Math.min(value, x), Infinity);
  const top = curve.reduce((value, { y }) => Math.min(value, y), Infinity);
  const right = curve.reduce((value, { x }) => Math.max(value, x), -Infinity);
  const bottom = curve.reduce((value, { y }) => Math.max(value, y), -Infinity);

  return { x: left, y: top, width: right - left, height: bottom - top };
}

function curveIntersectsRect(curve: Position[], rect: Rect): boolean {
  return curve.some((point, index) => index > 0 && segmentIntersectsRect(curve[index - 1], point, rect));
}

/**
 * リンクの線が、端点ではない付箋を横切っていないか検査する。
 *
 * ラベルの重なりと違って文字は隠れないが、線がどの要素をつないでいるのか読み取れなくなる。
 * 判定には描画と同じベジエ曲線を使うため、bend で付箋を迂回しているリンクは報告しない。
 *
 * ゾーンは背景として描かれ、リンクがその上を通ることを前提にしているので対象にしない。
 *
 * targetIds を渡すと、そのリンクか端点が対象に含まれるものだけを報告する。横切られた付箋を
 * 動かしたときにも報告する必要があるため、交差した付箋も対象として扱う。
 */
function checkLinkNodeCrossing(
  links: LinkForSpacing[],
  endpoints: Map<string, LinkEndpointElement>,
  viewPath: string,
  nodeSize: { width: number; height: number },
  targetIds?: Set<string>
): QualityWarning[] {
  const warnings: QualityWarning[] = [];

  for (const link of links) {
    if (link.from === link.to) continue;

    const from = endpoints.get(link.from);
    const to = endpoints.get(link.to);
    // 端点が見つからないリンクは参照整合性の検査が報告する
    if (!from || !to) continue;
    // 端点同士が重なっている場合は重なりの検査が報告するので、ここでは扱わない
    if (rectsOverlap(from.rect, to.rect)) continue;
    if (!isFiniteRect(from.rect) || !isFiniteRect(to.rect)) continue;

    const isTargeted = !targetIds || targetIds.has(link.id) || targetIds.has(link.from) || targetIds.has(link.to);
    const candidates = [...endpoints].filter(
      ([elementId, element]) =>
        element.isNode &&
        elementId !== link.from &&
        elementId !== link.to &&
        isFiniteRect(element.rect) &&
        (isTargeted || targetIds?.has(elementId))
    );
    // 曲線の構築は安くないので、報告しうる付箋が無いリンクでは組まない
    if (candidates.length === 0) continue;

    const curve = sampleLinkCurve(from.rect, to.rect, link.bend, nodeSize);
    if (!curve) continue;
    const curveBounds = curveBoundingRect(curve);
    const label = link.label?.trim();
    const linkName = linkDisplayName(label, from, to);

    for (const [, element] of candidates) {
      if (!rectsOverlap(curveBounds, element.rect) || !curveIntersectsRect(curve, element.rect)) continue;

      warnings.push({
        path: `${viewPath}.links`,
        message:
          `${linkName}の線が、端点ではない${element.label}を横切っています: ` +
          `その付箋か両端の要素を動かすか、リンクを曲げて線の通り道を空けてください`,
        kind: 'layout',
      });
    }
  }

  return warnings;
}

type ElementDescriptionInput = { content: string; relativePosition?: { x: number; y: number }; visible?: boolean };

type ElementDescriptionOwner = {
  id: string;
  label: string;
  // 警告のパス。同じ名前の要素が複数あってもAIが補足説明の持ち主を特定できるようにする
  path: string;
  isZone: boolean;
  position: { x: number; y: number };
  size: { width: number; height: number };
  description?: ElementDescriptionInput;
};

type PlacedElementDescription = {
  ownerId: string;
  ownerLabel: string;
  path: string;
  side: string;
  rect: Rect;
};

/**
 * 補足説明の高さを概算する。幅は固定で、本文は文字単位で折り返される（break-all）ため、
 * 本文の幅に収まらない文字が来るたびに行を送って行数を数える。
 */
function estimateElementDescriptionSize(content: string): Size {
  const { width: calloutWidth, height: minHeight } = ElementDescriptionLayout.initialSize();
  const textWidth = calloutWidth - ELEMENT_DESCRIPTION_PADDING * 2;
  let lineCount = 0;
  for (const line of content.split('\n')) {
    lineCount++;
    let lineWidth = 0;
    for (const char of line) {
      const charWidth = isFullWidthChar(char) ? ELEMENT_DESCRIPTION_FONT_SIZE : ELEMENT_DESCRIPTION_HALF_WIDTH;
      if (lineWidth > 0 && lineWidth + charWidth > textWidth) {
        lineCount++;
        lineWidth = 0;
      }
      lineWidth += charWidth;
    }
  }
  return new Size(
    calloutWidth,
    Math.max(minHeight, lineCount * ELEMENT_DESCRIPTION_LINE_HEIGHT + ELEMENT_DESCRIPTION_PADDING * 2)
  );
}

function describeSide(rect: Rect, ownerRect: Rect): string {
  const horizontal = rect.x >= ownerRect.x + ownerRect.width ? '右' : rect.x + rect.width <= ownerRect.x ? '左' : '';
  const vertical = rect.y >= ownerRect.y + ownerRect.height ? '下' : rect.y + rect.height <= ownerRect.y ? '上' : '';
  return horizontal + vertical;
}

/**
 * 補足説明が描画される矩形を求める。
 *
 * 保存された位置はそのまま使われず、描画時に要素の周囲へ吸着するため、描画と同じ調整をかける。
 * 位置・表示状態が省略された場合は、既存の補足説明の値、それも無ければ新規作成時の初期位置・表示になる。
 * 非表示の補足説明と本文が空の補足説明（作成されない）は描画されないので返さない。
 */
function placeElementDescriptions(
  owners: ElementDescriptionOwner[],
  nodeSize: { width: number; height: number },
  previousDescriptions?: ReadonlyMap<string, ElementDescriptionInput>
): PlacedElementDescription[] {
  const placed: PlacedElementDescription[] = [];

  for (const owner of owners) {
    const { description } = owner;
    if (!description?.content) continue;
    const previous = previousDescriptions?.get(owner.id);
    if (!(description.visible ?? previous?.visible ?? true)) continue;

    const ownerRect = toRect(owner);
    if (!isFiniteRect(ownerRect)) continue;

    // 新規の補足説明の初期位置は、実際のゾーンではなくゾーンの既定サイズを基準に決まる
    const relativePosition =
      description.relativePosition ??
      previous?.relativePosition ??
      ElementDescriptionLayout.initialRelativePosition(owner.isZone ? StickyZoneSize.buildNew() : nodeSize);
    const size = estimateElementDescriptionSize(description.content);
    const { x, y } = ElementDescriptionLayout.createPositionAdjuster(
      new Point(ownerRect.x + relativePosition.x, ownerRect.y + relativePosition.y),
      size
    ).adjustPositionTo(toBasicRect(ownerRect));
    const rect = { x, y, width: size.width, height: size.height };
    if (!isFiniteRect(rect)) continue;

    placed.push({
      ownerId: owner.id,
      ownerLabel: owner.label,
      path: owner.path,
      side: describeSide(rect, ownerRect),
      rect,
    });
  }

  return placed;
}

function toElementDescriptionOwners(
  elements: {
    nodes: {
      id: string;
      content: string;
      position: Position;
      size: { width: number; height: number };
      description?: ElementDescriptionInput;
    }[];
    zones: {
      id: string;
      name: string;
      position: Position;
      size: { width: number; height: number };
      description?: ElementDescriptionInput;
    }[];
  },
  pathOf: (kind: 'nodes' | 'zones', index: number) => string
): ElementDescriptionOwner[] {
  return [
    ...elements.nodes.map((node, index) => ({
      id: node.id,
      label: `付箋"${node.content}"`,
      path: pathOf('nodes', index),
      isZone: false,
      position: node.position,
      size: node.size,
      description: node.description,
    })),
    ...elements.zones.map((zone, index) => ({
      id: zone.id,
      label: `ゾーン"${zone.name}"`,
      path: pathOf('zones', index),
      isZone: true,
      position: zone.position,
      size: zone.size,
      description: zone.description,
    })),
  ];
}

type ZoneForDescriptionOverlap = {
  id: string;
  name: string;
  position: Position;
  size: { width: number; height: number };
  style?: { shape?: 'Rectangle' | 'Circle' };
};

/**
 * AIの編集を適用するとゾーンの重なり順は配列の順と包含関係から組み直されるため、適用時と同じ方法で求める。
 */
function orderZonesBackToFront<T extends ZoneForDescriptionOverlap>(zones: T[]): T[] {
  const zoneById = new Map(zones.map((zone) => [zone.id, zone]));
  return buildDisplayOrderTreeByContainment(
    [],
    zones.map((zone) => ({ id: zone.id, rect: toBasicRect(toRect(zone)) }))
  )
    .backToFrontZone()
    .flatMap((id) => zoneById.get(id) ?? []);
}

/**
 * 補足説明（吹き出し）が付箋・説明パネル・リンクのラベル・他の補足説明に重なっていないか、
 * ビューからはみ出していないかを検査する。
 *
 * 補足説明は付箋やゾーンと同じ層に描かれるため、重なるとどちらかの文字が隠れる。吹き出しの高さは
 * 本文の量で決まり、AIからは見えないため、高さの目安を添えて報告する。
 *
 * ゾーンは、ゾーンの補足説明より手前に描かれて吹き出しを隠すものだけを報告する。付箋の補足説明はゾーンより
 * 手前に描かれ、ゾーン内の付箋の補足説明がゾーンの内側に表示されるのは普通のことなので対象にしない。
 * リンクも線との重なりは対象にせず、不透明な矩形として描かれるラベルとの重なりだけを報告する。
 *
 * targetIds を渡すと、補足説明の持ち主か重なった相手（リンクのラベルはリンクかその端点）が対象に
 * 含まれるものだけを報告する。パッチの
 * 適用ゲートで、触っていない既存要素の指摘を返さないために使う。ただしビューのサイズを変える場合は、
 * サイズを決めるのに収まらない吹き出しをすべて知る必要があるため、右端・下端へのはみ出しは対象外のものも報告する。
 */
function checkElementDescriptionOverlaps(
  descriptions: PlacedElementDescription[],
  obstacles: {
    nodes: { id: string; content: string; position: Position; size: { width: number; height: number } }[];
    descriptionPanels: { id: string; position: Position; size: { width: number; height: number } }[];
    linkLabels: PlacedLinkLabel[];
    zonesBackToFront: ZoneForDescriptionOverlap[];
  },
  view: { size: { width: number; height: number }; isResized?: boolean } | undefined,
  targetIds?: Set<string>
): QualityWarning[] {
  const warnings: QualityWarning[] = [];
  const isTargeted = (...ids: string[]) => !targetIds || ids.some((id) => targetIds.has(id));
  const describe = (description: PlacedElementDescription) =>
    `${description.ownerLabel}の補足説明（幅${description.rect.width}px・高さ約${description.rect.height}pxの吹き出しとして` +
    `${description.ownerLabel}の${description.side || '周囲'}に表示されます）`;
  const remedy =
    'description.relativePosition で吹き出しを要素の別の辺へ移すか、要素を動かして吹き出しの分の空きを作ってください';
  const zoneDrawIndex = new Map(obstacles.zonesBackToFront.map((zone, index) => [zone.id, index]));

  for (const description of descriptions) {
    if (view && !isInsideWithMargin(description.rect, view.size, 0)) {
      if (isTargeted(description.ownerId)) {
        warnings.push({
          path: description.path,
          message: `${describe(description)}がビューの範囲からはみ出しています。${remedy}`,
          kind: 'layout',
        });
      } else if (view.isResized && exceedsRightOrBottomEdge(description.rect, view.size, 0)) {
        warnings.push({
          path: description.path,
          message: `${describe(description)}が変更後のビューの右端か下端からはみ出しています。ビューのサイズを大きくするか、${remedy}`,
          kind: 'layout',
        });
      }
    }

    for (const node of obstacles.nodes) {
      if (node.id === description.ownerId || !isTargeted(description.ownerId, node.id)) continue;
      if (!rectsOverlap(description.rect, toRect(node))) continue;

      warnings.push({
        path: description.path,
        message: `${describe(description)}が付箋"${node.content}"に重なって読めません。${remedy}`,
        kind: 'layout',
      });
    }

    for (const panel of obstacles.descriptionPanels) {
      if (!isTargeted(description.ownerId, panel.id)) continue;
      if (!rectsOverlap(description.rect, toRect(panel))) continue;

      warnings.push({
        path: description.path,
        message: `${describe(description)}が説明パネルに重なって読めません。${remedy}`,
        kind: 'layout',
      });
    }

    for (const linkLabel of obstacles.linkLabels) {
      if (!isTargeted(description.ownerId, ...linkLabel.relatedIds)) continue;
      if (!rectsOverlap(description.rect, linkLabel.rect)) continue;

      warnings.push({
        path: description.path,
        message:
          `${describe(description)}が${linkLabel.linkName}のラベルに重なって読めません。` +
          'description.relativePosition で吹き出しを要素の別の辺へ移すか、リンクの曲げ(bend)でラベルの位置をずらしてください',
        kind: 'layout',
      });
    }

    const ownerZoneIndex = zoneDrawIndex.get(description.ownerId);
    if (ownerZoneIndex !== undefined) {
      for (const zone of obstacles.zonesBackToFront.slice(ownerZoneIndex + 1)) {
        if (!isTargeted(description.ownerId, zone.id)) continue;
        const hidden =
          zone.style?.shape === 'Circle'
            ? rectOverlapsEllipse(description.rect, toRect(zone))
            : rectsOverlap(description.rect, toRect(zone));
        if (!hidden) continue;

        warnings.push({
          path: description.path,
          message: `${describe(description)}が手前に描かれるゾーン"${zone.name}"に隠れて読めません。${remedy}`,
          kind: 'layout',
        });
      }
    }
  }

  for (let index = 0; index < descriptions.length; index++) {
    const first = descriptions[index];
    for (let otherIndex = index + 1; otherIndex < descriptions.length; otherIndex++) {
      const second = descriptions[otherIndex];
      if (!isTargeted(first.ownerId, second.ownerId)) continue;
      if (!rectsOverlap(first.rect, second.rect)) continue;

      warnings.push({
        path: first.path,
        message: `${describe(first)}と${describe(second)}が重なって読めません。${remedy}`,
        kind: 'layout',
      });
    }
  }

  return warnings;
}

/**
 * キャンバス上でのビューの配置をチェックする。
 * addView ではAIが指定した position にビューが置かれるため、既存ビューや同時に追加する
 * ビューと重ならないことを検証する。
 */
export function checkViewPlacementQuality(
  views: AIViewModelContextView[],
  existingViews?: PlacedView[]
): QualityWarning[] {
  const warnings: QualityWarning[] = [];

  for (const [index, view] of views.entries()) {
    const viewPath = `add[${index}]`;

    if (view.position.x % VIEW_GRID_SIZE !== 0 || view.position.y % VIEW_GRID_SIZE !== 0) {
      warnings.push({
        path: `${viewPath}.position`,
        message: `ビュー"${view.name}"のキャンバス上の位置が${VIEW_GRID_SIZE}の倍数ではありません: (${view.position.x}, ${view.position.y})`,
        kind: 'layout',
      });
    }

    for (const existingView of existingViews ?? []) {
      if (rectsOverlap(toRect(view), toRect(existingView))) {
        warnings.push({
          path: `${viewPath}.position`,
          message: `ビュー"${view.name}"が既存のビュー"${existingView.name}"と重なっています`,
          kind: 'layout',
        });
      } else if (rectsWithinMargin(toRect(view), toRect(existingView), VIEW_MIN_MARGIN)) {
        warnings.push({
          path: `${viewPath}.position`,
          message: `ビュー"${view.name}"と既存のビュー"${existingView.name}"の間隔が最低マージン(${VIEW_MIN_MARGIN}px)未満です`,
          kind: 'layout',
        });
      }
    }
  }

  // 同時に追加するビュー同士のチェック
  for (let i = 0; i < views.length; i++) {
    const a = views[i];
    for (let j = i + 1; j < views.length; j++) {
      const b = views[j];
      if (rectsOverlap(toRect(a), toRect(b))) {
        warnings.push({
          path: `add[${i}].position`,
          message: `ビュー"${a.name}"と"${b.name}"が重なっています`,
          kind: 'layout',
        });
      } else if (rectsWithinMargin(toRect(a), toRect(b), VIEW_MIN_MARGIN)) {
        warnings.push({
          path: `add[${i}].position`,
          message: `ビュー"${a.name}"と"${b.name}"の間隔が最低マージン(${VIEW_MIN_MARGIN}px)未満です`,
          kind: 'layout',
        });
      }
    }
  }

  return warnings;
}

export function describeViewsOutsideCanvas(views: PlacedView[]): string | null {
  const { min, max } = VIEW_MODEL_CANVAS_BOUNDS;
  const outsideViews = views
    .map((view) => ({ name: view.name, rect: toRect(view) }))
    .filter(({ rect }) => rect.x < min || rect.y < min || rect.x + rect.width > max || rect.y + rect.height > max);

  if (outsideViews.length === 0) return null;

  return [
    `キャンバスの範囲（x・y ともに${min}〜${max}）からはみ出すビューがあるため、ビューを作成しませんでした。`,
    ...outsideViews.map(
      ({ rect, name }) =>
        `- ビュー"${name}": 左上(${rect.x}, ${rect.y})〜右下(${rect.x + rect.width}, ${rect.y + rect.height})`
    ),
    'ビュー全体がキャンバスの範囲に収まる position を指定して、すべてのビューをもう一度 addView で追加してください。',
  ].join('\n');
}

/**
 * ビューの品質チェックを行い、レイアウトの問題を警告として返す。
 */
export function checkViewQuality(
  view: AIViewModelContextView,
  viewIndex: number,
  options?: QualityCheckOptions
): QualityWarning[] {
  const warnings: QualityWarning[] = [];
  const viewPath = `add[${viewIndex}]`;
  const viewSize = { width: view.size.width, height: view.size.height };
  const nodeSize = resolveNodeSize(options);

  // ビューサイズチェック
  if (view.size.width < 3584) {
    warnings.push({
      path: `${viewPath}.size.width`,
      message: `ビューの幅が最低値(3584)未満です: ${view.size.width}`,
      kind: 'layout',
    });
  }
  if (view.size.height < 2048) {
    warnings.push({
      path: `${viewPath}.size.height`,
      message: `ビューの高さが最低値(2048)未満です: ${view.size.height}`,
      kind: 'layout',
    });
  }
  if (view.size.width % VIEW_GRID_SIZE !== 0) {
    warnings.push({
      path: `${viewPath}.size.width`,
      message: `ビューの幅が${VIEW_GRID_SIZE}の倍数ではありません: ${view.size.width}`,
      kind: 'layout',
    });
  }
  if (view.size.height % VIEW_GRID_SIZE !== 0) {
    warnings.push({
      path: `${viewPath}.size.height`,
      message: `ビューの高さが${VIEW_GRID_SIZE}の倍数ではありません: ${view.size.height}`,
      kind: 'layout',
    });
  }

  // 付箋サイズ・位置チェック
  for (let i = 0; i < view.nodes.length; i++) {
    const node = view.nodes[i];
    if (node.size.width !== nodeSize.width || node.size.height !== nodeSize.height) {
      warnings.push({
        path: `${viewPath}.nodes[${i}]`,
        message: `付箋"${node.content}"のサイズが${nodeSize.width}x${nodeSize.height}ではありません: ${node.size.width}x${node.size.height}`,
        kind: 'layout',
      });
    }
    checkPositionOnGrid(warnings, `${viewPath}.nodes[${i}].position`, `付箋"${node.content}"`, node.position);
  }

  // ゾーンサイズ（モデルグリッドの倍数）・位置チェック
  for (let i = 0; i < view.zones.length; i++) {
    const zone = view.zones[i];
    if (zone.size.width % MODEL_GRID_SIZE !== 0) {
      warnings.push({
        path: `${viewPath}.zones[${i}].size.width`,
        message: `ゾーン"${zone.name}"の幅が${MODEL_GRID_SIZE}の倍数ではありません: ${zone.size.width}`,
        kind: 'layout',
      });
    }
    if (zone.size.height % MODEL_GRID_SIZE !== 0) {
      warnings.push({
        path: `${viewPath}.zones[${i}].size.height`,
        message: `ゾーン"${zone.name}"の高さが${MODEL_GRID_SIZE}の倍数ではありません: ${zone.size.height}`,
        kind: 'layout',
      });
    }
    checkPositionOnGrid(warnings, `${viewPath}.zones[${i}].position`, `ゾーン"${zone.name}"`, zone.position);
  }

  // ゾーンのサイズ（内部要素に対して大きすぎないか）と内側マージンのチェック
  const zoneContentCandidates = toZoneContentCandidates(view);
  warnings.push(...checkZoneSizeAgainstContents(view.zones, zoneContentCandidates, viewPath));
  warnings.push(...checkZoneInnerMargins(view.zones, zoneContentCandidates, viewPath));

  // リンクの長さ・ラベルの収まり・線の通り道のチェック
  const linkEndpoints = toLinkEndpointMap(view);
  warnings.push(...checkLinkSpacing(view.links, linkEndpoints, viewPath));
  warnings.push(...checkLinkNodeCrossing(view.links, linkEndpoints, viewPath, nodeSize));

  // 補足説明の重なり・はみ出しのチェック
  const elementDescriptions = placeElementDescriptions(
    toElementDescriptionOwners(view, (kind, index) => `${viewPath}.${kind}[${index}].description`),
    nodeSize
  );
  warnings.push(
    ...checkElementDescriptionOverlaps(
      elementDescriptions,
      {
        ...view,
        linkLabels: placeLinkLabels(view.links, linkEndpoints),
        zonesBackToFront: orderZonesBackToFront(view.zones),
      },
      { size: viewSize }
    )
  );

  // 付箋同士の重なりチェック
  for (let i = 0; i < view.nodes.length; i++) {
    const a = view.nodes[i];
    const rectA: Rect = { x: a.position.x, y: a.position.y, width: a.size.width, height: a.size.height };
    for (let j = i + 1; j < view.nodes.length; j++) {
      const b = view.nodes[j];
      const rectB: Rect = { x: b.position.x, y: b.position.y, width: b.size.width, height: b.size.height };
      if (rectsOverlap(rectA, rectB)) {
        warnings.push({
          path: `${viewPath}.nodes`,
          message: `付箋"${a.content}"と"${b.content}"が重なっています`,
          kind: 'layout',
        });
      }
    }
  }

  // 付箋同士の最低マージンチェック
  const NODE_MIN_MARGIN = MODEL_GRID_SIZE;
  for (let i = 0; i < view.nodes.length; i++) {
    const a = view.nodes[i];
    const rectA: Rect = { x: a.position.x, y: a.position.y, width: a.size.width, height: a.size.height };
    for (let j = i + 1; j < view.nodes.length; j++) {
      const b = view.nodes[j];
      const rectB: Rect = { x: b.position.x, y: b.position.y, width: b.size.width, height: b.size.height };
      if (!rectsOverlap(rectA, rectB) && rectsWithinMargin(rectA, rectB, NODE_MIN_MARGIN)) {
        warnings.push({
          path: `${viewPath}.nodes`,
          message: `付箋"${a.content}"と"${b.content}"の間隔が最低マージン(${NODE_MIN_MARGIN}px)未満です`,
          kind: 'layout',
        });
      }
    }
  }

  // ゾーン同士の重なりチェック
  for (let i = 0; i < view.zones.length; i++) {
    const a = view.zones[i];
    const rectA: Rect = { x: a.position.x, y: a.position.y, width: a.size.width, height: a.size.height };
    for (let j = i + 1; j < view.zones.length; j++) {
      const b = view.zones[j];
      const rectB: Rect = { x: b.position.x, y: b.position.y, width: b.size.width, height: b.size.height };
      if (rectsOverlap(rectA, rectB)) {
        warnings.push({
          path: `${viewPath}.zones`,
          message: `ゾーン"${a.name}"と"${b.name}"が重なっています`,
          kind: 'layout',
        });
      }
    }
  }

  // ビュー範囲内チェック（ビュー端からのマージン）
  for (let i = 0; i < view.nodes.length; i++) {
    const node = view.nodes[i];
    const nodeRect: Rect = { x: node.position.x, y: node.position.y, width: node.size.width, height: node.size.height };
    if (!isInsideWithMargin(nodeRect, viewSize, VIEW_EDGE_MARGIN)) {
      warnings.push({
        path: `${viewPath}.nodes[${i}]`,
        message: `付箋"${node.content}"がビューの範囲外か、ビュー端から${VIEW_EDGE_MARGIN}pxのマージンが確保されていません`,
        kind: 'layout',
      });
    }
  }
  for (let i = 0; i < view.zones.length; i++) {
    const zone = view.zones[i];
    const zoneRect: Rect = { x: zone.position.x, y: zone.position.y, width: zone.size.width, height: zone.size.height };
    if (!isInsideWithMargin(zoneRect, viewSize, VIEW_EDGE_MARGIN)) {
      warnings.push({
        path: `${viewPath}.zones[${i}]`,
        message: `ゾーン"${zone.name}"がビューの範囲外か、ビュー端から${VIEW_EDGE_MARGIN}pxのマージンが確保されていません`,
        kind: 'layout',
      });
    }
  }
  for (let i = 0; i < view.descriptionPanels.length; i++) {
    const panel = view.descriptionPanels[i];
    const panelRect: Rect = {
      x: panel.position.x,
      y: panel.position.y,
      width: panel.size.width,
      height: panel.size.height,
    };
    if (!isInsideWithMargin(panelRect, viewSize, VIEW_EDGE_MARGIN)) {
      warnings.push({
        path: `${viewPath}.descriptionPanels[${i}]`,
        message: `説明パネルがビューの範囲外か、ビュー端から${VIEW_EDGE_MARGIN}pxのマージンが確保されていません`,
        kind: 'layout',
      });
    }
  }

  // リンクの参照整合性チェック
  const elementIds = new Set<string>();
  for (const node of view.nodes) elementIds.add(node.id);
  for (const zone of view.zones) elementIds.add(zone.id);
  for (let i = 0; i < view.links.length; i++) {
    const link = view.links[i];
    if (!elementIds.has(link.from)) {
      warnings.push({
        path: `${viewPath}.links[${i}].from`,
        message: `リンクのfrom "${link.from}" が同じビュー内の付箋・ゾーンのIDと一致しません`,
        kind: 'linkReference',
      });
    }
    if (!elementIds.has(link.to)) {
      warnings.push({
        path: `${viewPath}.links[${i}].to`,
        message: `リンクのto "${link.to}" が同じビュー内の付箋・ゾーンのIDと一致しません`,
        kind: 'linkReference',
      });
    }
  }

  // ID重複チェック
  const allIds: string[] = [];
  for (const node of view.nodes) allIds.push(node.id);
  for (const zone of view.zones) allIds.push(zone.id);
  for (const link of view.links) allIds.push(link.id);
  for (const panel of view.descriptionPanels) allIds.push(panel.id);
  const seenIds = new Set<string>();
  for (const id of allIds) {
    if (seenIds.has(id)) {
      warnings.push({ path: viewPath, message: `ID "${id}" が重複しています`, kind: 'duplicateId' });
    }
    seenIds.add(id);
  }

  return warnings;
}

/**
 * patchViewの差分データに対する品質チェックを行う。
 * 提供された要素のみを対象にチェックする（既存ビューの内容は不明なため完全なチェックはできない）。
 */
export function checkPatchViewQuality(
  patchView: AIViewModelPatchView,
  viewIndex: number,
  options?: QualityCheckOptions,
  existingViewSize?: AIViewModelContextView['size']
): QualityWarning[] {
  const warnings: QualityWarning[] = [];
  const viewPath = `patch[${viewIndex}]`;
  const nodeSize = resolveNodeSize(options);

  const nodes = patchView.nodes ?? [];
  const links = patchView.links ?? [];
  const zones = patchView.zones ?? [];
  const descriptionPanels = patchView.descriptionPanels ?? [];

  // ビューサイズチェック（指定されている場合のみ）
  if (patchView.size) {
    if (patchView.size.width < 3584) {
      warnings.push({
        path: `${viewPath}.size.width`,
        message: `ビューの幅が最低値(3584)未満です: ${patchView.size.width}`,
        kind: 'layout',
      });
    }
    if (patchView.size.height < 2048) {
      warnings.push({
        path: `${viewPath}.size.height`,
        message: `ビューの高さが最低値(2048)未満です: ${patchView.size.height}`,
        kind: 'layout',
      });
    }
    if (patchView.size.width % VIEW_GRID_SIZE !== 0) {
      warnings.push({
        path: `${viewPath}.size.width`,
        message: `ビューの幅が${VIEW_GRID_SIZE}の倍数ではありません: ${patchView.size.width}`,
        kind: 'layout',
      });
    }
    if (patchView.size.height % VIEW_GRID_SIZE !== 0) {
      warnings.push({
        path: `${viewPath}.size.height`,
        message: `ビューの高さが${VIEW_GRID_SIZE}の倍数ではありません: ${patchView.size.height}`,
        kind: 'layout',
      });
    }
  }

  // 付箋サイズ・位置チェック
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node.size.width !== nodeSize.width || node.size.height !== nodeSize.height) {
      warnings.push({
        path: `${viewPath}.nodes[${i}]`,
        message: `付箋"${node.content}"のサイズが${nodeSize.width}x${nodeSize.height}ではありません: ${node.size.width}x${node.size.height}`,
        kind: 'layout',
      });
    }
    checkPositionOnGrid(warnings, `${viewPath}.nodes[${i}].position`, `付箋"${node.content}"`, node.position);
  }

  // ゾーンサイズ（モデルグリッドの倍数）・位置チェック
  // ゾーンが内部要素に対して大きすぎないかは、差分だけでは内部要素が分からないため
  // 適用ゲート(evaluatePatchForApply)で既存ビューと合わせて検査する。
  for (let i = 0; i < zones.length; i++) {
    const zone = zones[i];
    if (zone.size.width % MODEL_GRID_SIZE !== 0) {
      warnings.push({
        path: `${viewPath}.zones[${i}].size.width`,
        message: `ゾーン"${zone.name}"の幅が${MODEL_GRID_SIZE}の倍数ではありません: ${zone.size.width}`,
        kind: 'layout',
      });
    }
    if (zone.size.height % MODEL_GRID_SIZE !== 0) {
      warnings.push({
        path: `${viewPath}.zones[${i}].size.height`,
        message: `ゾーン"${zone.name}"の高さが${MODEL_GRID_SIZE}の倍数ではありません: ${zone.size.height}`,
        kind: 'layout',
      });
    }
    checkPositionOnGrid(warnings, `${viewPath}.zones[${i}].position`, `ゾーン"${zone.name}"`, zone.position);
  }

  // パッチ内の付箋同士の重なりチェック
  for (let i = 0; i < nodes.length; i++) {
    const a = nodes[i];
    const rectA: Rect = { x: a.position.x, y: a.position.y, width: a.size.width, height: a.size.height };
    for (let j = i + 1; j < nodes.length; j++) {
      const b = nodes[j];
      const rectB: Rect = { x: b.position.x, y: b.position.y, width: b.size.width, height: b.size.height };
      if (rectsOverlap(rectA, rectB)) {
        warnings.push({
          path: `${viewPath}.nodes`,
          message: `付箋"${a.content}"と"${b.content}"が重なっています`,
          kind: 'layout',
        });
      }
    }
  }

  // パッチ内の付箋同士の最低マージンチェック
  const NODE_MIN_MARGIN = MODEL_GRID_SIZE;
  for (let i = 0; i < nodes.length; i++) {
    const a = nodes[i];
    const rectA: Rect = { x: a.position.x, y: a.position.y, width: a.size.width, height: a.size.height };
    for (let j = i + 1; j < nodes.length; j++) {
      const b = nodes[j];
      const rectB: Rect = { x: b.position.x, y: b.position.y, width: b.size.width, height: b.size.height };
      if (!rectsOverlap(rectA, rectB) && rectsWithinMargin(rectA, rectB, NODE_MIN_MARGIN)) {
        warnings.push({
          path: `${viewPath}.nodes`,
          message: `付箋"${a.content}"と"${b.content}"の間隔が最低マージン(${NODE_MIN_MARGIN}px)未満です`,
          kind: 'layout',
        });
      }
    }
  }

  // パッチ内のゾーン同士の重なりチェック
  for (let i = 0; i < zones.length; i++) {
    const a = zones[i];
    const rectA: Rect = { x: a.position.x, y: a.position.y, width: a.size.width, height: a.size.height };
    for (let j = i + 1; j < zones.length; j++) {
      const b = zones[j];
      const rectB: Rect = { x: b.position.x, y: b.position.y, width: b.size.width, height: b.size.height };
      if (rectsOverlap(rectA, rectB)) {
        warnings.push({
          path: `${viewPath}.zones`,
          message: `ゾーン"${a.name}"と"${b.name}"が重なっています`,
          kind: 'layout',
        });
      }
    }
  }

  // ビュー範囲内チェック
  const viewSize = patchView.size ?? existingViewSize;
  if (viewSize) {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const nodeRect: Rect = {
        x: node.position.x,
        y: node.position.y,
        width: node.size.width,
        height: node.size.height,
      };
      if (!isInsideWithMargin(nodeRect, viewSize, VIEW_EDGE_MARGIN)) {
        warnings.push({
          path: `${viewPath}.nodes[${i}]`,
          message: `付箋"${node.content}"がビュー端から${VIEW_EDGE_MARGIN}pxのマージンが確保されていません`,
          kind: 'layout',
        });
      }
    }
    for (let i = 0; i < zones.length; i++) {
      const zone = zones[i];
      const zoneRect: Rect = {
        x: zone.position.x,
        y: zone.position.y,
        width: zone.size.width,
        height: zone.size.height,
      };
      if (!isInsideWithMargin(zoneRect, viewSize, VIEW_EDGE_MARGIN)) {
        warnings.push({
          path: `${viewPath}.zones[${i}]`,
          message: `ゾーン"${zone.name}"がビュー端から${VIEW_EDGE_MARGIN}pxのマージンが確保されていません`,
          kind: 'layout',
        });
      }
    }
    for (let i = 0; i < descriptionPanels.length; i++) {
      const panel = descriptionPanels[i];
      const panelRect: Rect = {
        x: panel.position.x,
        y: panel.position.y,
        width: panel.size.width,
        height: panel.size.height,
      };
      if (!isInsideWithMargin(panelRect, viewSize, VIEW_EDGE_MARGIN)) {
        warnings.push({
          path: `${viewPath}.descriptionPanels[${i}]`,
          message: `説明パネルがビュー端から${VIEW_EDGE_MARGIN}pxのマージンが確保されていません`,
          kind: 'layout',
        });
      }
    }
  }

  // パッチ内のリンク参照整合性チェック（パッチ内の要素同士のみ）
  // リンクの長さとラベルの収まりは、差分だけでは端点の要素が分からないため
  // 適用ゲート(evaluatePatchForApply)で既存ビューと合わせて検査する。
  const patchElementIds = new Set<string>();
  for (const node of nodes) patchElementIds.add(node.id);
  for (const zone of zones) patchElementIds.add(zone.id);
  for (let i = 0; i < links.length; i++) {
    const link = links[i];
    // パッチ内に from/to の参照先がない場合、既存ビューの要素を参照している可能性があるので警告ではなく情報として扱う
    // ただし、from と to の両方がパッチ内に存在しないのは明らかにおかしいので警告する
    const fromInPatch = patchElementIds.has(link.from);
    const toInPatch = patchElementIds.has(link.to);
    if (!fromInPatch && !toInPatch) {
      warnings.push({
        path: `${viewPath}.links[${i}]`,
        message: `リンクのfrom "${link.from}" と to "${link.to}" がどちらもパッチ内の要素IDと一致しません`,
        kind: 'linkReference',
      });
    }
  }

  // パッチ内のID重複チェック
  const allIds: string[] = [];
  for (const node of nodes) allIds.push(node.id);
  for (const zone of zones) allIds.push(zone.id);
  for (const link of links) allIds.push(link.id);
  for (const panel of descriptionPanels) allIds.push(panel.id);
  const seenIds = new Set<string>();
  for (const id of allIds) {
    if (seenIds.has(id)) {
      warnings.push({ path: viewPath, message: `ID "${id}" が重複しています`, kind: 'duplicateId' });
    }
    seenIds.add(id);
  }

  return warnings;
}

/**
 * 適用ゲートの判定結果。
 *
 * 適用を止める経路は持たない。ID重複と存在しない要素へのリンクは、かつては「データの壊れ」として
 * 適用せずAIへ差し戻していたが、AIが差し戻しの理由を解消できないケース（別のビューの要素を端点に
 * 指定している、採番済みIDを使い回している等）では同じ編集を投げ直すループになり、
 * 巨大なJSONの再生成と履歴の再送でトークンを浪費していた。どちらも機械的に修復できるため、
 * 修復した上で適用し、修復内容を repairs としてAIに伝える方式に変えている。
 */
export interface ApplyEvaluation {
  /** 自動修復した内容。適用は済んでいるので、AIは意図と違う場合だけ直せばよい。 */
  repairs: QualityWarning[];
  /**
   * レイアウトの粗さ（重なり・マージン・サイズ・グリッド・範囲・孤立ノード）。
   * AIは既存要素の正確な座標を把握しづらいため、適用した上で注意として返す。
   */
  advisory: QualityWarning[];
}

const ELEMENT_KIND_LABELS = {
  nodes: '付箋',
  zones: 'ゾーン',
  links: 'リンク',
  descriptionPanels: '説明パネル',
} as const;

type ElementKind = keyof typeof ELEMENT_KIND_LABELS;

function describeLink(link: { from: string; to: string; label?: string }): string {
  const label = link.label?.trim();
  return label ? `リンク"${label}"（${link.from} → ${link.to}）` : `リンク（${link.from} → ${link.to}）`;
}

// 端点候補としてAIに提示するIDの最大件数。全件並べるとビューの規模に比例してツール結果が肥大するため、
// 収まらない分は getCurrentViews で取り直させる。
const MAX_ENDPOINT_HINTS = 30;

/**
 * リンクを落としたときに、代わりに使える端点のIDをAIへ提示する。
 * 「存在しないIDだ」とだけ伝えても何なら使えるのかが分からず、同じIDでの張り直しを招くため。
 */
function buildEndpointHint(viewPath: string, endpoints: { id: string; label: string }[]): QualityWarning {
  const shown = endpoints.slice(0, MAX_ENDPOINT_HINTS).map((endpoint) => `"${endpoint.id}"=${endpoint.label}`);
  const omitted = endpoints.length - shown.length;
  return {
    path: `${viewPath}.links`,
    message:
      `このビューでリンクの端点に使えるIDは次のとおりです: ${shown.join(', ')}` +
      (omitted > 0 ? `, ほか${omitted}件（全件は getCurrentViews で取得してください）` : ''),
    kind: 'linkReference',
  };
}

type RepairContext = {
  viewPath: string;
  generateId: () => string;
  repairs: QualityWarning[];
};

/**
 * 同じ編集内でIDが重複している要素に、新しいIDを振り直す。
 *
 * 重複したIDを指しているリンクの端点は書き換えない。どちらの要素を指していたのかは入力からは
 * 決められないため、そのIDを最初に使った要素を指すものとして扱い、振り直した事実を修復内容として
 * AIに伝えて判断を委ねる。
 */
function reissueDuplicateId<E extends { id: string }>(
  element: E,
  label: string,
  kind: ElementKind,
  seenIds: Set<string>,
  context: RepairContext
): E {
  if (!seenIds.has(element.id)) {
    seenIds.add(element.id);
    return element;
  }

  const newId = context.generateId();
  seenIds.add(newId);
  context.repairs.push({
    path: `${context.viewPath}.${kind}`,
    message:
      `${label}のID "${element.id}" が同じ編集内の他の要素と重複していたため、新しいID "${newId}" を割り当てて適用しました。` +
      `リンクの端点はこのIDを最初に使った要素を指しています。意図と違う場合は張り直してください`,
    kind: 'duplicateId',
  });
  return { ...element, id: newId } as E;
}

/**
 * 端点が存在しないリンクを落とす。落としたリンクは修復内容としてAIに伝える。
 */
function dropDanglingLinks<L extends { id: string; from: string; to: string; label?: string }>(
  links: L[],
  endpoints: { id: string; label: string }[],
  context: RepairContext
): L[] {
  const endpointIds = new Set(endpoints.map((endpoint) => endpoint.id));
  const kept: L[] = [];
  let dropped = false;

  for (const link of links) {
    const missingId = [link.from, link.to].find((id) => !endpointIds.has(id));
    if (missingId === undefined) {
      kept.push(link);
      continue;
    }
    dropped = true;
    context.repairs.push({
      path: `${context.viewPath}.links`,
      message:
        `${describeLink(link)}は端点 "${missingId}" が存在しないため、このリンクだけ適用しませんでした。` +
        `リンクの端点は同じビュー内の付箋・ゾーンのIDでなければならず、ビューをまたいでつなぐことはできません`,
      kind: 'linkReference',
    });
  }

  if (dropped) context.repairs.push(buildEndpointHint(context.viewPath, endpoints));
  return kept;
}

/**
 * addView の内容を、そのまま適用してもデータが壊れない状態に修復する。
 *
 * generateId には generateIds ツールと同じ形式のIDを返す関数を渡す（ViewModelPageOperation が
 * 新規要素のID形式を検証するため）。
 */
function repairEditViewsForApply<T extends AIViewModelContextView>(
  views: T[],
  generateId: () => string
): { views: T[]; repairs: QualityWarning[] } {
  const repairs: QualityWarning[] = [];

  const repairedViews = views.map((view, index) => {
    const context: RepairContext = { viewPath: `add[${index}]`, generateId, repairs };
    const seenIds = new Set<string>();

    const nodes = view.nodes.map((node) =>
      reissueDuplicateId(node, `付箋"${node.content}"`, 'nodes', seenIds, context)
    );
    const zones = view.zones.map((zone) => reissueDuplicateId(zone, `ゾーン"${zone.name}"`, 'zones', seenIds, context));
    const descriptionPanels = view.descriptionPanels.map((panel) =>
      reissueDuplicateId(panel, '説明パネル', 'descriptionPanels', seenIds, context)
    );
    const links = view.links.map((link) => reissueDuplicateId(link, describeLink(link), 'links', seenIds, context));

    const endpoints = [
      ...nodes.map((node) => ({ id: node.id, label: `付箋"${node.content}"` })),
      ...zones.map((zone) => ({ id: zone.id, label: `ゾーン"${zone.name}"` })),
    ];

    return {
      ...view,
      nodes,
      zones,
      descriptionPanels,
      links: dropDanglingLinks(links, endpoints, context),
    } as T;
  });

  return { views: repairedViews, repairs };
}

/**
 * パッチの要素が既存の「別種の」要素とIDを衝突させている場合に、新しいIDを振り直す。
 *
 * 要素のIDは種別をまたいで一意でなければならない。patchView の removeIds が種別共通で
 * 全種類の要素を消すことに加え、DisplayOrderTree が付箋IDとゾーンIDを同じID空間として
 * 扱う（ancestors / hasNode / hasZone）ため、別種の要素がIDを共有すると所属や表示順の
 * 判定が壊れる。
 *
 * 同種の要素との一致は既存要素の更新なので衝突ではない。removeIds で消える既存要素も
 * 適用後には存在しないため対象外とする。
 *
 * 振り直したIDが付箋・ゾーンの場合は、同じパッチ内でその要素を端点にしているリンクも
 * 新しいIDへ書き換える。衝突していたのは既存の別種要素との間だけで、パッチ内での参照先は
 * 一意に決まるため。
 */
function repairCrossKindIdConflicts<T extends AIViewModelPatchView>(
  patchView: T,
  existing: AIViewModelContextView,
  removeIds: Set<string>,
  context: RepairContext
): T {
  const existingKindById = new Map<string, ElementKind>();
  for (const kind of Object.keys(ELEMENT_KIND_LABELS) as ElementKind[]) {
    for (const element of existing[kind]) {
      if (!removeIds.has(element.id)) existingKindById.set(element.id, kind);
    }
  }

  const reassignedEndpointIds = new Map<string, string>();
  const reassign = <E extends { id: string }>(kind: ElementKind, element: E): E => {
    const existingKind = existingKindById.get(element.id);
    if (existingKind === undefined || existingKind === kind) return element;

    const newId = context.generateId();
    if (kind === 'nodes' || kind === 'zones') reassignedEndpointIds.set(element.id, newId);
    context.repairs.push({
      path: `${context.viewPath}.${kind}`,
      message:
        `ID "${element.id}" は既存の${ELEMENT_KIND_LABELS[existingKind]}のIDのため、` +
        `${ELEMENT_KIND_LABELS[kind]}には使えません。新しいID "${newId}" を割り当てて適用しました。` +
        `新規要素には generateIds で採番したIDを使用してください`,
      kind: 'duplicateId',
    });
    return { ...element, id: newId } as E;
  };

  const nodes = patchView.nodes?.map((node) => reassign('nodes', node));
  const zones = patchView.zones?.map((zone) => reassign('zones', zone));
  const descriptionPanels = patchView.descriptionPanels?.map((panel) => reassign('descriptionPanels', panel));
  const links = patchView.links
    ?.map((link) => reassign('links', link))
    .map((link) => ({
      ...link,
      from: reassignedEndpointIds.get(link.from) ?? link.from,
      to: reassignedEndpointIds.get(link.to) ?? link.to,
    }));

  return { ...patchView, nodes, zones, links, descriptionPanels } as T;
}

/**
 * patchView の内容を、そのまま適用してもデータが壊れない状態に修復する。
 *
 * 既存ビューが渡されなかった場合（取得に失敗した場合など）は、差分内だけで判断できる
 * ID重複の修復にとどめる。既存要素を端点にした正しいリンクを、判定材料がないまま
 * 落としてしまわないようにするため。
 */
function repairPatchViewsForApply<T extends AIViewModelPatchView>(
  patchViews: T[],
  existingViewsById: Record<string, AIViewModelContextView | undefined>,
  generateId: () => string
): { views: T[]; repairs: QualityWarning[] } {
  const repairs: QualityWarning[] = [];

  const repairedViews = patchViews.map((patchView, index) => {
    const context: RepairContext = { viewPath: `patch[${index}]`, generateId, repairs };
    const existing = existingViewsById[patchView.id];
    const removeIds = new Set(patchView.removeIds ?? []);

    const crossKindRepaired = existing
      ? repairCrossKindIdConflicts(patchView, existing, removeIds, context)
      : patchView;

    const seenIds = new Set<string>();
    const nodes = crossKindRepaired.nodes?.map((node) =>
      reissueDuplicateId(node, `付箋"${node.content}"`, 'nodes', seenIds, context)
    );
    const zones = crossKindRepaired.zones?.map((zone) =>
      reissueDuplicateId(zone, `ゾーン"${zone.name}"`, 'zones', seenIds, context)
    );
    const descriptionPanels = crossKindRepaired.descriptionPanels?.map((panel) =>
      reissueDuplicateId(panel, '説明パネル', 'descriptionPanels', seenIds, context)
    );
    const links = crossKindRepaired.links?.map((link) =>
      reissueDuplicateId(link, describeLink(link), 'links', seenIds, context)
    );

    const repaired = { ...crossKindRepaired, nodes, zones, links, descriptionPanels } as T;
    if (!existing || !links) return repaired;

    // 適用後に存在する付箋・ゾーンだけがリンクの端点になれる。
    const endpoints = [
      ...existing.nodes
        .filter((node) => !removeIds.has(node.id) && !nodes?.some((patched) => patched.id === node.id))
        .map((node) => ({ id: node.id, label: `付箋"${node.content}"` })),
      ...existing.zones
        .filter((zone) => !removeIds.has(zone.id) && !zones?.some((patched) => patched.id === zone.id))
        .map((zone) => ({ id: zone.id, label: `ゾーン"${zone.name}"` })),
      ...(nodes ?? [])
        .filter((node) => !removeIds.has(node.id))
        .map((node) => ({ id: node.id, label: `付箋"${node.content}"` })),
      ...(zones ?? [])
        .filter((zone) => !removeIds.has(zone.id))
        .map((zone) => ({ id: zone.id, label: `ゾーン"${zone.name}"` })),
    ];
    // removeIds で消すリンクは端点が消えていても問題ないため、修復の対象から外す。
    const removedLinks = links.filter((link) => removeIds.has(link.id));
    const targetLinks = links.filter((link) => !removeIds.has(link.id));

    return { ...repaired, links: [...dropDanglingLinks(targetLinks, endpoints, context), ...removedLinks] } as T;
  });

  return { views: repairedViews, repairs };
}

// サイズを決めるには収まらない要素をすべて知る必要があるため、変更前からはみ出していた要素も報告する。
// 左端・上端のはみ出しはサイズを変えても直らないため対象にしない
function checkUntouchedElementsInsideResizedView(
  elements: {
    nodes: { content: string; position: Position; size: { width: number; height: number } }[];
    zones: { name: string; position: Position; size: { width: number; height: number } }[];
    descriptionPanels: { position: Position; size: { width: number; height: number } }[];
  },
  viewSize: { width: number; height: number },
  viewPath: string
): QualityWarning[] {
  const labeledElements = [
    ...elements.nodes.map((node) => ({ label: `既存の付箋"${node.content}"`, rect: toRect(node) })),
    ...elements.zones.map((zone) => ({ label: `既存のゾーン"${zone.name}"`, rect: toRect(zone) })),
    ...elements.descriptionPanels.map((panel) => ({ label: '既存の説明パネル', rect: toRect(panel) })),
  ];
  return labeledElements
    .filter(({ rect }) => exceedsRightOrBottomEdge(rect, viewSize, VIEW_EDGE_MARGIN))
    .map(({ label }) => ({
      path: `${viewPath}.size`,
      message:
        `${label}が変更後のビューの右端か下端からはみ出しているか、ビュー端から${VIEW_EDGE_MARGIN}pxのマージンが確保されていません。` +
        'ビューのサイズを大きくするか、要素を動かしてください',
      kind: 'layout' as const,
    }));
}

/** ビュー内で、どのリンクにも繋がっていない付箋を孤立ノードとして列挙する（advisory）。 */
function checkOrphanNodes(
  nodes: { id: string; content: string }[],
  links: { from: string; to: string }[],
  viewPath: string
): QualityWarning[] {
  const referenced = new Set<string>();
  for (const link of links) {
    referenced.add(link.from);
    referenced.add(link.to);
  }
  const warnings: QualityWarning[] = [];
  for (const node of nodes) {
    if (!referenced.has(node.id)) {
      warnings.push({
        path: `${viewPath}.nodes`,
        message: `付箋"${node.content}"がどのリンクにも繋がっていません（孤立）`,
        kind: 'layout',
      });
    }
  }
  return warnings;
}

/**
 * addView の適用前評価。
 * ビューの内容がすべて渡されるので、checkViewQuality をそのまま適用ゲートの判定に使える。
 *
 * existingViews を渡した場合は、AIが指定した position に置かれるビューが
 * キャンバス上で既存ビューと重なっていないかも検査する。
 */
export function evaluateEditForApply<T extends AIViewModelContextView>(
  views: T[],
  options: QualityCheckOptions & { existingViews?: PlacedView[]; generateId: () => string }
): ApplyEvaluation & { views: T[] } {
  const { views: repairedViews, repairs } = repairEditViewsForApply(views, options.generateId);
  const advisory: QualityWarning[] = [];

  for (let i = 0; i < repairedViews.length; i++) {
    const view = repairedViews[i];
    advisory.push(...checkViewQuality(view, i, options));
    advisory.push(...checkOrphanNodes(view.nodes, view.links, `add[${i}]`));
  }

  if (options?.existingViews) {
    advisory.push(...checkViewPlacementQuality(repairedViews, options.existingViews));
  }

  return { views: repairedViews, repairs, advisory };
}

/**
 * patchView の適用前評価。
 *
 * 差分だけでは判定できない検査を、呼び出し側から渡された既存ビューで補う:
 * - リンクの端点は「適用後に存在する付箋・ゾーン」であること（既存要素へ張る正しいリンクを誤検知しない）
 * - パッチの要素が既存要素と重なっていないか
 * - パッチで追加した付箋が、既存リンクも含めて孤立していないか
 * - パッチでサイズを省略した場合も、パッチの要素がビューの範囲内にあるか
 * - パッチでビューのサイズを変える場合は、パッチが触っていない既存要素も変更後のビューに収まるか
 *
 * 既存ビューが渡されなかった場合は差分内の検査だけを行う（判定材料がないものは指摘しない）。
 */
export function evaluatePatchForApply<T extends AIViewModelPatchView>(
  patchViews: T[],
  existingViewsById: Record<string, AIViewModelContextView | undefined>,
  options: QualityCheckOptions & { generateId: () => string }
): ApplyEvaluation & { views: T[] } {
  const { views: repairedViews, repairs } = repairPatchViewsForApply(patchViews, existingViewsById, options.generateId);
  const advisory: QualityWarning[] = [];

  for (let i = 0; i < repairedViews.length; i++) {
    const patchView = repairedViews[i];
    const viewPath = `patch[${i}]`;
    const existing = existingViewsById[patchView.id];
    const removeIds = new Set(patchView.removeIds ?? []);
    const nodes = (patchView.nodes ?? []).filter((node) => !removeIds.has(node.id));
    const zones = (patchView.zones ?? []).filter((zone) => !removeIds.has(zone.id));

    for (const warning of checkPatchViewQuality(patchView, i, options, existing?.size)) {
      // リンク端点の検査は差分内だけでは既存要素への正しいリンクを誤検知するため、
      // 既存ビューを含めた修復（repairPatchViewsForApply）に任せてここでは捨てる。
      if (warning.kind === 'linkReference') continue;
      advisory.push(warning);
    }

    if (!existing) continue;

    // パッチの要素と、パッチが触っていない既存要素との重なり
    const patchNodeIds = new Set(nodes.map((node) => node.id));
    const untouchedNodes = existing.nodes.filter((node) => !patchNodeIds.has(node.id) && !removeIds.has(node.id));
    for (const patchNode of nodes) {
      const rect = toRect(patchNode);
      for (const existingNode of untouchedNodes) {
        if (rectsOverlap(rect, toRect(existingNode))) {
          advisory.push({
            path: `${viewPath}.nodes`,
            message: `付箋"${patchNode.content}"が既存の付箋"${existingNode.content}"と重なっています`,
            kind: 'layout',
          });
        }
      }
    }
    const patchZoneIds = new Set(zones.map((zone) => zone.id));
    const untouchedZones = existing.zones.filter((zone) => !patchZoneIds.has(zone.id) && !removeIds.has(zone.id));
    for (const patchZone of zones) {
      const rect = toRect(patchZone);
      for (const existingZone of untouchedZones) {
        if (rectsOverlap(rect, toRect(existingZone))) {
          advisory.push({
            path: `${viewPath}.zones`,
            message: `ゾーン"${patchZone.name}"が既存のゾーン"${existingZone.name}"と重なっています`,
            kind: 'layout',
          });
        }
      }
    }

    // ゾーンのサイズと内側マージンは内部要素が分からないと判定できないため、適用後の要素集合
    // （据え置きの既存要素＋パッチの要素）で判定する。
    // 注意を返すのはパッチが触った要素が関係する組み合わせだけにする（触っていない既存要素同士を
    // 毎回指摘しても編集の手掛かりにならないため）。
    const patchPanels = (patchView.descriptionPanels ?? []).filter((panel) => !removeIds.has(panel.id));
    const patchPanelIds = new Set(patchPanels.map((panel) => panel.id));
    const untouchedPanels = existing.descriptionPanels.filter(
      (panel) => !patchPanelIds.has(panel.id) && !removeIds.has(panel.id)
    );
    // ゾーンの重なり順は並びで決まるため、適用時と同じく既存の並びのまま更新し、新しいゾーンを末尾に足す
    const patchZoneById = new Map(zones.map((zone) => [zone.id, zone]));
    const resultingZones = [
      ...existing.zones.filter((zone) => !removeIds.has(zone.id)).map((zone) => patchZoneById.get(zone.id) ?? zone),
      ...zones.filter((zone) => !existing.zones.some((existingZone) => existingZone.id === zone.id)),
    ];
    const zoneContentCandidates = toZoneContentCandidates({
      nodes: [...untouchedNodes, ...nodes],
      zones: resultingZones,
      descriptionPanels: [...untouchedPanels, ...patchPanels],
    });
    const patchedIds = new Set([...nodes, ...zones, ...patchPanels].map((element) => element.id));
    advisory.push(...checkZoneSizeAgainstContents(zones, zoneContentCandidates, viewPath));
    advisory.push(...checkZoneInnerMargins(resultingZones, zoneContentCandidates, viewPath, patchedIds));

    const isViewResized =
      patchView.size !== undefined &&
      (patchView.size.width !== existing.size.width || patchView.size.height !== existing.size.height);
    if (isViewResized && patchView.size) {
      advisory.push(
        ...checkUntouchedElementsInsideResizedView(
          { nodes: untouchedNodes, zones: untouchedZones, descriptionPanels: untouchedPanels },
          patchView.size,
          viewPath
        )
      );
    }

    // 適用後のリンク集合（据え置きの既存リンク＋パッチのリンク）。同じIDがパッチにあれば更新後の内容を使う。
    // ただし bend の省略は「現在の曲げを維持する」という意味なので、既存リンクの bend を引き継ぐ
    const existingLinksById = new Map(existing.links.map((link) => [link.id, link]));
    const patchLinks = (patchView.links ?? [])
      .filter((link) => !removeIds.has(link.id))
      .map((link) => {
        const inheritedBend = link.bend ?? existingLinksById.get(link.id)?.bend;
        return inheritedBend ? { ...link, bend: inheritedBend } : link;
      });
    const patchLinkIds = new Set(patchLinks.map((link) => link.id));
    const resultingLinks = [
      ...existing.links.filter((link) => !removeIds.has(link.id) && !patchLinkIds.has(link.id)),
      ...patchLinks,
    ];

    // リンクの長さ・ラベルの収まり・線の通り道も、端点の要素が分からないと判定できないため
    // 適用後の要素集合で判定する。パッチがリンク自体か端点の要素を触ったものだけを報告する。
    const linkEndpoints = toLinkEndpointMap({ nodes: [...untouchedNodes, ...nodes], zones: resultingZones });
    const linkTargetIds = new Set([...patchedIds, ...patchLinkIds]);
    advisory.push(...checkLinkSpacing(resultingLinks, linkEndpoints, viewPath, linkTargetIds));
    advisory.push(
      ...checkLinkNodeCrossing(resultingLinks, linkEndpoints, viewPath, resolveNodeSize(options), linkTargetIds)
    );

    // 補足説明も、吹き出しの位置が持ち主の要素で決まり、重なる相手も既存要素でありうるため、
    // 適用後の要素集合で判定する。パッチが補足説明の持ち主か重なった相手を触ったものだけを報告する。
    const existingDescriptions = new Map(
      [...existing.nodes, ...existing.zones].flatMap((element) =>
        element.description ? [[element.id, element.description] as const] : []
      )
    );
    const elementDescriptions = placeElementDescriptions(
      [
        ...toElementDescriptionOwners(
          { nodes: untouchedNodes, zones: untouchedZones },
          (kind) => `${viewPath}.${kind}`
        ),
        ...toElementDescriptionOwners(
          { nodes: patchView.nodes ?? [], zones: patchView.zones ?? [] },
          (kind, index) => `${viewPath}.${kind}[${index}].description`
        ).filter((owner) => !removeIds.has(owner.id)),
      ],
      resolveNodeSize(options),
      existingDescriptions
    );
    advisory.push(
      ...checkElementDescriptionOverlaps(
        elementDescriptions,
        {
          nodes: [...untouchedNodes, ...nodes],
          descriptionPanels: [...untouchedPanels, ...patchPanels],
          linkLabels: placeLinkLabels(resultingLinks, linkEndpoints),
          zonesBackToFront: orderZonesBackToFront(resultingZones),
        },
        { size: patchView.size ?? existing.size, isResized: isViewResized },
        linkTargetIds
      )
    );

    // パッチが追加した付箋の孤立を、適用後のリンク集合で判定する
    advisory.push(...checkOrphanNodes(nodes, resultingLinks, viewPath));
  }

  return { views: repairedViews, repairs, advisory };
}

/**
 * 1つの区分につき並べる指摘の上限。
 * 大きな編集では同種の指摘が数十件出ることがあり、全件並べてもAIの修正には繋がらず
 * ツール結果が肥大してトークンを消費するだけなので打ち切る。
 */
const MAX_WARNING_LINES = 20;

/**
 * 指摘の一覧を行に整形する。path を添えるのは、要素名だけでは「同じ名前の付箋が複数ある」
 * ような場合にAIがどれを直せばよいか特定できないため。
 */
function formatWarningLines(warnings: QualityWarning[]): string {
  const shown = warnings.slice(0, MAX_WARNING_LINES).map((warning) => `- [${warning.path}] ${warning.message}`);
  const omitted = warnings.length - shown.length;
  return [...shown, ...(omitted > 0 ? [`- ほか${omitted}件`] : [])].join('\n');
}

/** 適用ゲートの判定結果を、AIへ返すツール実行結果の文言に整形する。 */
export function formatApplyEvaluation(evaluation: ApplyEvaluation): { repairText: string; advisoryText: string } {
  const repairText =
    evaluation.repairs.length > 0
      ? '次の問題を自動的に修正した上で適用しました。修正した結果が意図と違う場合だけ、続けて修正してください' +
        '（同じ内容で呼び出し直しても同じ修正が行われるだけです）:\n' +
        formatWarningLines(evaluation.repairs)
      : '';
  const advisoryText =
    evaluation.advisory.length > 0
      ? '注意（適用は済みましたが、次の点を確認してください）:\n' + formatWarningLines(evaluation.advisory)
      : '';
  return { repairText, advisoryText };
}
